import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ExtensionMeta } from '../extension';
import type { ExtensionSdk } from './types';

interface ExtensionContextValue {
  sdk: ExtensionSdk;
  meta: ExtensionMeta;
  config: unknown;
}

const ExtensionContext = createContext<ExtensionContextValue | null>(null);

interface ProviderProps {
  sdk: ExtensionSdk;
  meta: ExtensionMeta;
  children: ReactNode;
}

/** Validates the config against meta.config and exposes sdk + config to the extension. */
export function ExtensionProvider({ sdk, meta, children }: ProviderProps) {
  const parsed = useMemo(() => meta.config.safeParse(sdk.config ?? {}), [meta, sdk.config]);

  useEffect(() => { sdk.autoResize(); }, [sdk]);

  if (!parsed.success) {
    return (
      <div className="vc-error" role="alert">
        <strong>Invalid config params for “{meta.title}”</strong>
        <ul>
          {parsed.error.issues.map((i, n) => (
            <li key={n}><code>{i.path.join('.') || '(root)'}</code>: {i.message}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <ExtensionContext.Provider value={{ sdk, meta, config: parsed.data }}>
      {children}
    </ExtensionContext.Provider>
  );
}

function useExtensionContext(): ExtensionContextValue {
  const ctx = useContext(ExtensionContext);
  if (!ctx) throw new Error('useSdk/useConfig/useFieldValue must be used inside <ExtensionProvider>');
  return ctx;
}

export function useSdk(): ExtensionSdk {
  return useExtensionContext().sdk;
}

/** Validated config. Type it with ConfigOf<typeof meta>. */
export function useConfig<T>(): T {
  return useExtensionContext().config as T;
}

/** Two-way binding with the custom field's value. */
export function useFieldValue<T>(initial: T): [T, (next: T) => Promise<void>] {
  const { sdk } = useExtensionContext();
  const field = sdk.field;
  if (!field) throw new Error('useFieldValue is only available in custom_field extensions');

  const [value, setLocal] = useState<T>(() => {
    const current = field.get();
    return current === undefined || current === null || current === '' ? initial : (current as T);
  });

  useEffect(() => {
    field.onChange((v) => setLocal(v as T));
  }, [field]);

  const setValue = useCallback(async (next: T) => {
    setLocal(next);
    await field.set(next);
  }, [field]);

  return [value, setValue];
}
